/**
 * Ported VERBATIM from `apr_template/src/pages/ScoringAuditTrail.tsx`.
 * Only import paths change. See ../compat/ for the shims.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import apiService from '../compat/api';
import PageHeader from '../compat/PageHeader';
import HelpPanel from '../compat/HelpPanel';
import { type Help } from '../compat/helpTypes';

/** One row from the scoring_change_log (server: GET /assessments/scoring-audit). */
interface Change {
  id: string;
  application_id: string;
  application_id_code: string | null;
  application_name: string | null;
  apr_run_id: string | null;
  apr_run_name: string | null;
  dimension_name: string | null;
  component_name: string | null;
  component_weight: number | null;
  question_text: string | null;
  old_answer_text: string | null;
  old_answer_score: number | null;
  old_weighted_score: number | null;
  new_answer_text: string | null;
  new_answer_score: number | null;
  new_weighted_score: number | null;
  changed_by_name: string | null;
  changed_by_email: string | null;
  note: string | null;
  changed_at: string;
}

const AuditHelp: Help = {
  title: 'Scoring Audit Trail',
  icon: '🧾',
  purpose:
    'A complete, append-only record of every change to a Q&amp;A answer — grouped by application. For each change you see the question, the original answer and its score, the new answer and its score, and who made the change, when.',
  doHere: [
    'Changes are <b>grouped by application</b> — each application shows how many changes it has had; its changes are listed newest first.',
    'Each row shows the <b>question</b>, the <b>original answer &amp; score</b> → the <b>new answer &amp; score</b>, and <b>who</b> changed it <b>when</b>.',
    'Click a row (or the 👁 icon) to open the full detail — including the component weight and the weighted score before and after.',
    'Filter by application, user, or question text to narrow the list.',
  ],
  fits: 'Application Evaluation &rarr; Audit Trail',
  firstTime: 'Every answer change made from the Q&amp;A screen is recorded here automatically — no setup needed.',
  sections: [
    { type: 'html', label: 'What is captured', html:
      'A change is logged whenever a Q&amp;A answer is saved and the selected option changes (including the first answer to a question). The record keeps the framework context (dimension, component, component weight) and both the answer score and the weighted score (score &times; weight &divide; 5) before and after.' },
    { type: 'html', label: 'Recording starts now', html:
      'The trail records changes going forward from when it was enabled — earlier edits made before then are not shown, because prior answers were overwritten without history.' },
  ],
  links: [
    { href: '/evaluation/qa', label: 'Application Portfolio Q&A' },
    { href: '/evaluation/scoring', label: 'Initial Portfolio Scoring' },
  ],
};

// score → colored pill (matches the app's badge convention)
const scoreStyle = (s: number | null): React.CSSProperties => {
  const base: React.CSSProperties = { display: 'inline-grid', placeItems: 'center', minWidth: 22, height: 22, padding: '0 6px', borderRadius: 12, fontSize: 12, fontWeight: 800, fontFamily: 'ui-monospace, Menlo, monospace', border: '1px solid transparent' };
  if (s == null) return { ...base, background: '#eceff1', color: '#8a95a8', borderColor: '#dde3ea' };
  if (s <= 2) return { ...base, background: '#fdecec', color: '#c0392b', borderColor: '#f4c7c7' };
  if (s === 3) return { ...base, background: '#fff3cd', color: '#9a6b00', borderColor: '#f3d9a6' };
  return { ...base, background: '#dcfce7', color: '#15803d', borderColor: '#bfe3c2' };
};
const ScoreChip: React.FC<{ s: number | null }> = ({ s }) => <span style={scoreStyle(s)}>{s ?? '—'}</span>;

const fmtDate = (s: string) => { try { return new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); } catch { return s; } };
const fmtTime = (s: string) => { try { return new Date(s).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }); } catch { return ''; } };
const fmtLong = (s: string) => { try { return new Date(s).toLocaleString('en-US', { month: 'long', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }); } catch { return s; } };
const num2 = (v: any) => (v == null || v === '' ? '—' : Number(v).toFixed(2));

// Locked-header two-table layout — column widths shared by header + body (AMD pattern).
const COLS = ['13%', '15%', '33%', '33%', '6%'];
const Cols = () => (<colgroup>{COLS.map((w, i) => <col key={i} style={{ width: w }} />)}</colgroup>);
const th: React.CSSProperties = { backgroundColor: '#e8e8e8', borderBottom: '2px solid #bbb', padding: '10px 8px', fontWeight: 700, fontSize: 13, color: '#333', textAlign: 'left' };
const td: React.CSSProperties = { padding: '8px 8px', borderBottom: '1px solid #dee2e6', fontSize: 13, verticalAlign: 'middle' };

const ScoringAuditTrail: React.FC = () => {
  const [changes, setChanges] = useState<Change[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterText, setFilterText] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [open, setOpen] = useState<Change | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try { setChanges(await apiService.getScoringAudit()); }
    catch (e: any) { setError(e?.response?.data?.error || e?.message || 'Failed to load the audit trail.'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const users = useMemo(() => Array.from(new Set(changes.map((c) => c.changed_by_name).filter(Boolean))).sort() as string[], [changes]);

  const filtered = useMemo(() => {
    const q = filterText.trim().toLowerCase();
    return changes.filter((c) => {
      if (userFilter && c.changed_by_name !== userFilter) return false;
      if (q) {
        const hay = `${c.application_id_code || ''} ${c.application_name || ''} ${c.changed_by_name || ''} ${c.question_text || ''} ${c.dimension_name || ''} ${c.component_name || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [changes, filterText, userFilter]);

  // group by application, newest-first within each group and across groups
  const groups = useMemo(() => {
    const m = new Map<string, { code: string; name: string; items: Change[] }>();
    for (const c of filtered) {
      const key = c.application_id_code || c.application_id;
      if (!m.has(key)) m.set(key, { code: c.application_id_code || '—', name: c.application_name || '', items: [] });
      m.get(key)!.items.push(c);
    }
    const arr = Array.from(m.values());
    arr.forEach((g) => g.items.sort((a, b) => b.changed_at.localeCompare(a.changed_at)));
    arr.sort((a, b) => (b.items[0]?.changed_at || '').localeCompare(a.items[0]?.changed_at || ''));
    return arr;
  }, [filtered]);

  const today = useMemo(() => { const d = new Date().toISOString().slice(0, 10); return changes.filter((c) => (c.changed_at || '').slice(0, 10) === d).length; }, [changes]);
  const appsAffected = useMemo(() => new Set(changes.map((c) => c.application_id_code || c.application_id)).size, [changes]);
  const contributors = useMemo(() => new Set(changes.map((c) => c.changed_by_name).filter(Boolean)).size, [changes]);

  return (
    <div className="page page-fixed-header">
      <HelpPanel help={AuditHelp} />

      <div className="fixed-header-section">
        <PageHeader
          title="Scoring Audit Trail"
          subtitle="Every change to a Q&A answer — the question, the original answer and score, the new answer and score, and who changed it when."
        />

        <div className="master-data-stats">
          <div className="stat-item"><span className="stat-value">{changes.length}</span><span className="stat-label">Total changes</span></div>
          <div className="stat-item"><span className="stat-value">{today}</span><span className="stat-label">Changes today</span></div>
          <div className="stat-item"><span className="stat-value">{appsAffected}</span><span className="stat-label">Applications affected</span></div>
          <div className="stat-item"><span className="stat-value">{contributors}</span><span className="stat-label">Contributors</span></div>
        </div>

        {/* Filter bar (AMD convention: inline, #ddd idle / 2px #1976d2 active, 4px radius) */}
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #dee2e6', flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: 1, maxWidth: 400 }}>
            <input
              type="text"
              placeholder="Filter by application, ID, user, or question…"
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              style={{ width: '100%', padding: '6px 30px 6px 10px', border: '1px solid #ddd', borderRadius: 4, fontSize: 15 }}
            />
            {filterText && (
              <button onClick={() => setFilterText('')} title="Clear filter"
                style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: '#666' }}>×</button>
            )}
          </div>
          <select value={userFilter} onChange={(e) => setUserFilter(e.target.value)}
            style={{ padding: '6px 10px', border: userFilter ? '2px solid #1976d2' : '1px solid #ddd', borderRadius: 4, fontSize: 15, maxWidth: 240 }}>
            <option value="">All users</option>
            {users.map((u) => <option key={u} value={u}>{u}</option>)}
          </select>
          <span style={{ fontSize: 13, color: '#666', marginLeft: 'auto' }}>
            {loading ? 'Loading…' : `${filtered.length} change${filtered.length === 1 ? '' : 's'} · ${groups.length} application${groups.length === 1 ? '' : 's'}`}
          </span>
        </div>
      </div>

      <div className="scrollable-data-section" style={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {error && <div style={{ color: '#c0392b', fontSize: 13, padding: '10px 0' }}>{error}</div>}

        {!loading && groups.length === 0 && !error ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#8a95a8' }}>
            No scoring changes recorded yet. Changes appear here as answers are saved on the <strong>Application Portfolio Q&A</strong> screen.
          </div>
        ) : (
          <div style={{ border: '1px solid #dee2e6', borderRadius: '0.5rem', backgroundColor: '#fff', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
            {/* Fixed header table */}
            <div style={{ flexShrink: 0 }}>
              <table className="master-data-table" style={{ tableLayout: 'fixed', width: '100%' }}>
                <Cols />
                <thead>
                  <tr>
                    <th style={th}>When</th>
                    <th style={th}>Who</th>
                    <th style={th}>Question</th>
                    <th style={th}>Answer change (original → new · score)</th>
                    <th style={{ ...th, textAlign: 'center' }}></th>
                  </tr>
                </thead>
              </table>
            </div>
            {/* Scrollable body table */}
            <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
              <table className="master-data-table" style={{ tableLayout: 'fixed', width: '100%' }}>
                <Cols />
                <tbody>
                  {groups.map((g) => (
                    <React.Fragment key={g.code}>
                      {/* Application group header row (parent-tinted, AMD convention) */}
                      <tr>
                        <td colSpan={5} style={{ backgroundColor: '#bbdefb', padding: '8px 10px', borderBottom: '1px solid #90caf9' }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                            <span style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontWeight: 700, color: '#0d47a1', fontSize: 13 }}>{g.code}</span>
                            <strong style={{ color: '#123', fontSize: 14 }}>{g.name}</strong>
                            <span style={{ padding: '2px 9px', fontSize: 11, fontWeight: 700, backgroundColor: '#1976d2', color: '#fff', borderRadius: 12 }}>
                              {g.items.length} change{g.items.length === 1 ? '' : 's'}
                            </span>
                          </span>
                        </td>
                      </tr>
                      {g.items.map((c) => {
                        const sel = open?.id === c.id;
                        const delta = (c.new_answer_score ?? 0) - (c.old_answer_score ?? 0);
                        return (
                          <tr key={c.id} onClick={() => setOpen(c)}
                            style={{ cursor: 'pointer', ...(sel ? { backgroundColor: '#fff3cd', boxShadow: 'inset 3px 0 0 #f59e0b' } : {}) }}>
                            <td style={{ ...td, paddingLeft: 34, borderLeft: '3px solid #e3f2fd' }}>
                              <div style={{ fontWeight: 700 }}>{fmtDate(c.changed_at)}</div>
                              <div style={{ color: '#8a95a8', fontSize: 11, fontFamily: 'ui-monospace, Menlo, monospace' }}>{fmtTime(c.changed_at)}</div>
                            </td>
                            <td style={td}>{c.changed_by_name || c.changed_by_email || '—'}</td>
                            <td style={td}>
                              <div style={{ fontWeight: 600, color: '#333' }}>{c.question_text || '—'}</div>
                              <div style={{ color: '#8a95a8', fontSize: 11.5, marginTop: 1 }}>{[c.dimension_name, c.component_name].filter(Boolean).join(' › ')}</div>
                            </td>
                            <td style={td}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap' }}>
                                <span style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0, maxWidth: '46%' }}>
                                  <span title={c.old_answer_text || 'Not answered'} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 12.5, color: c.old_answer_text ? '#333' : '#8a95a8' }}>{c.old_answer_text || 'Not answered'}</span>
                                  <ScoreChip s={c.old_answer_score} />
                                </span>
                                <span style={{ color: '#aeb8c7', flex: 'none' }}>→</span>
                                <span style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0, maxWidth: '46%' }}>
                                  <span title={c.new_answer_text || ''} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 12.5, color: '#333' }}>{c.new_answer_text || '—'}</span>
                                  <ScoreChip s={c.new_answer_score} />
                                </span>
                                {delta !== 0 && (
                                  <span style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontWeight: 800, fontSize: 11.5, color: delta > 0 ? '#15803d' : '#c0392b' }}>{delta > 0 ? '+' : ''}{delta}</span>
                                )}
                              </div>
                            </td>
                            <td style={{ ...td, textAlign: 'center' }}>
                              <button onClick={(e) => { e.stopPropagation(); setOpen(c); }} title="View detail"
                                style={{ background: 'transparent', border: '1px solid #1976d2', borderRadius: 4, padding: '4px 6px', cursor: 'pointer', fontSize: 14 }}>👁️</button>
                            </td>
                          </tr>
                        );
                      })}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {open && <ChangeDrawer c={open} onClose={() => setOpen(null)} />}
    </div>
  );
};

/** Right-side detail drawer — matches the Application Master Data drawer chrome. */
const ChangeDrawer: React.FC<{ c: Change; onClose: () => void }> = ({ c, onClose }) => {
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [onClose]);

  const card: React.CSSProperties = { border: '1px solid #e5e5e5', borderRadius: 10, padding: 12, background: '#fafbfc' };
  const lab: React.CSSProperties = { fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#8a95a8' };
  const secH: React.CSSProperties = { fontSize: 10.5, fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#1565c0', margin: '0 0 8px' };
  const mk: React.CSSProperties = { color: '#8a95a8', minWidth: 118, fontWeight: 600 };
  const delta = (c.new_answer_score ?? 0) - (c.old_answer_score ?? 0);

  return (
    <div role="dialog" aria-label="Scoring change detail"
      style={{ position: 'fixed', top: 0, right: 0, height: '100vh', width: 'min(540px, 96vw)', background: '#fff', boxShadow: '-4px 0 24px rgba(0,0,0,0.18)', zIndex: 1200, display: 'flex', flexDirection: 'column' }}>
      {/* Header — mirrors AMD drawer (title + Material Icons close X in a 36×36 red chip) */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: '1px solid #e5e5e5', flexShrink: 0 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontWeight: 700, color: '#1565c0', fontSize: 13 }}>{c.application_id_code || '—'}</span>
            <h2 style={{ margin: 0, fontSize: 17, fontWeight: 600, color: '#1a1a1a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.application_name}</h2>
          </div>
          <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{[c.dimension_name, c.component_name].filter(Boolean).join(' › ')}</div>
        </div>
        <button onClick={onClose} title="Close" aria-label="Close"
          style={{ width: 36, height: 36, borderRadius: 9, border: '1.5px solid #dc2626', background: '#fee2e2', color: '#b91c1c', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <i className="material-icons" style={{ fontSize: 20 }}>close</i>
        </button>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 18px' }}>
        <div style={{ marginBottom: 18 }}>
          <h4 style={secH}>Question changed</h4>
          <div style={{ background: '#f5f7fa', border: '1px solid #e5e5e5', borderRadius: 10, padding: '12px 14px', fontWeight: 600 }}>{c.question_text || '—'}</div>
        </div>

        <div style={{ marginBottom: 18 }}>
          <h4 style={secH}>Answer &amp; score</h4>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 10, alignItems: 'stretch' }}>
            <div style={{ ...card, borderColor: '#f4c7c7' }}>
              <div style={lab}>Original answer</div>
              <div style={{ fontWeight: 700, margin: '6px 0 8px' }}>{c.old_answer_text || 'Not answered'}</div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <span style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 22, fontWeight: 800, color: c.old_answer_score == null ? '#8a95a8' : c.old_answer_score <= 2 ? '#c0392b' : c.old_answer_score === 3 ? '#9a6b00' : '#15803d' }}>{c.old_answer_score ?? '—'}</span>
                <span style={{ color: '#8a95a8', fontFamily: 'ui-monospace, Menlo, monospace', fontWeight: 700 }}>/ 5</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#55627a', marginTop: 8 }}><span>Weighted</span><b style={{ fontFamily: 'ui-monospace, Menlo, monospace' }}>{num2(c.old_weighted_score)}</b></div>
            </div>
            <div style={{ display: 'grid', placeItems: 'center', color: '#aeb8c7' }}>→</div>
            <div style={{ ...card, borderColor: '#bfe3c2' }}>
              <div style={lab}>New answer</div>
              <div style={{ fontWeight: 700, margin: '6px 0 8px' }}>{c.new_answer_text || '—'}</div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <span style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 22, fontWeight: 800, color: c.new_answer_score == null ? '#8a95a8' : c.new_answer_score <= 2 ? '#c0392b' : c.new_answer_score === 3 ? '#9a6b00' : '#15803d' }}>{c.new_answer_score ?? '—'}</span>
                <span style={{ color: '#8a95a8', fontFamily: 'ui-monospace, Menlo, monospace', fontWeight: 700 }}>/ 5</span>
                {delta !== 0 && <span style={{ marginLeft: 4, fontFamily: 'ui-monospace, Menlo, monospace', fontWeight: 800, fontSize: 12, color: delta > 0 ? '#15803d' : '#c0392b' }}>{delta > 0 ? '▲ +' : '▼ '}{delta}</span>}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#55627a', marginTop: 8 }}><span>Weighted</span><b style={{ fontFamily: 'ui-monospace, Menlo, monospace' }}>{num2(c.new_weighted_score)}</b></div>
            </div>
          </div>
        </div>

        <div style={{ marginBottom: 18 }}>
          <h4 style={secH}>Change record</h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 13 }}>
            <div style={{ display: 'flex', gap: 10 }}><span style={mk}>Changed by</span><span>{c.changed_by_name || c.changed_by_email || '—'}</span></div>
            <div style={{ display: 'flex', gap: 10 }}><span style={mk}>When</span><span>{fmtLong(c.changed_at)}</span></div>
            <div style={{ display: 'flex', gap: 10 }}><span style={mk}>Dimension</span><span>{c.dimension_name || '—'}</span></div>
            <div style={{ display: 'flex', gap: 10 }}><span style={mk}>Component</span><span>{c.component_name || '—'}</span></div>
            <div style={{ display: 'flex', gap: 10 }}><span style={mk}>Comp. weight</span><span>{c.component_weight != null ? `${c.component_weight} / 5` : '—'}</span></div>
            <div style={{ display: 'flex', gap: 10 }}><span style={mk}>APR run</span><span>{c.apr_run_name || '—'}</span></div>
          </div>
        </div>

        {c.note && (
          <div style={{ marginBottom: 4 }}>
            <h4 style={secH}>Note</h4>
            <div style={{ background: '#f5f7fa', border: '1px solid #e5e5e5', borderLeft: '3px solid #1976d2', borderRadius: 8, padding: '10px 12px', color: '#55627a', fontSize: 13 }}>{c.note}</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ScoringAuditTrail;
